import { Input } from "@heroui/react";
import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../../pages/internal/YouWoConsumerLanding.css";
import ModernFooter from "../../components/landing/modern/ModernFooter";
import SEOHead from "../../components/tools/SEOHead";
import ToolsLandingNav from "../../components/tools/ToolsLandingNav";
import "../../components/tools/ToolsLandingShell.css";
import { buildAppLink } from "../../utils/landingRedirect";

interface Tool {
  name: string;
  description: string;
  href: string;
  icon: string;
  iconColor: string;
  bgColor: string;
  category: string;
  popular?: boolean;
  actionLabel?: string;
}

const tools: Tool[] = [
  {
    name: "YouTube Summarizer",
    description: "Transform long YouTube videos into concise summaries with timestamps and key points.",
    href: "/tools/youtube-summarizer",
    icon: "lucide:youtube",
    iconColor: "text-red-500",
    bgColor: "bg-red-50",
    category: "Summarization",
    popular: true,
  },
  {
    name: "PDF Summarizer",
    description: "Turn PDFs, research papers, and documents into clear summaries with source context.",
    href: "/tools/pdf-summarizer",
    icon: "lucide:file-text",
    iconColor: "text-orange-500",
    bgColor: "bg-orange-50",
    category: "Summarization",
    popular: true,
  },
  {
    name: "PDF to Study Guide",
    description: "Turn PDFs into source-cited study guides with key concepts, quiz questions, and flashcards.",
    href: "/tools/pdf-to-study-guide",
    icon: "lucide:book-open-check",
    iconColor: "text-cornflowerblue",
    bgColor: "bg-blue-50",
    category: "Study Tools",
  },
  {
    name: "AI Audio Transcriber",
    description: "Convert audio files and voice recordings to accurate text with speaker-aware context.",
    href: "/tools/audio-transcriber",
    icon: "lucide:mic",
    iconColor: "text-blue-500",
    bgColor: "bg-blue-50",
    category: "Transcription",
    popular: true,
  },
  {
    name: "AI Flashcard Maker",
    description: "Generate study flashcards automatically from text, PDFs, or notes.",
    href: "/tools/flashcard-maker",
    icon: "lucide:layers",
    iconColor: "text-purple-500",
    bgColor: "bg-purple-50",
    category: "Study Tools",
  },
  {
    name: "AI Quiz Generator",
    description: "Create practice quizzes with multiple choice, true or false, and short answer questions.",
    href: "/tools/quiz-generator",
    icon: "lucide:help-circle",
    iconColor: "text-green-500",
    bgColor: "bg-green-50",
    category: "Study Tools",
  },
  {
    name: "Meeting Notes Generator",
    description: "Convert meeting recordings into organized notes with action items and decisions.",
    href: "/tools/meeting-notes",
    icon: "lucide:users",
    iconColor: "text-indigo-500",
    bgColor: "bg-indigo-50",
    category: "Productivity",
  },
  {
    name: "Lecture Notes AI",
    description: "Transform lecture recordings and videos into comprehensive study notes.",
    href: "/tools/lecture-notes",
    icon: "lucide:graduation-cap",
    iconColor: "text-emerald-500",
    bgColor: "bg-emerald-50",
    category: "Study Tools",
  },
  {
    name: "YouWo vs ChatGPT",
    description: "Compare generic AI chat with source-cited studying from lectures, PDFs, videos, and notes.",
    href: "/compare/youwo-vs-chatgpt-for-studying",
    icon: "lucide:scale",
    iconColor: "text-slate-600",
    bgColor: "bg-slate-50",
    category: "Comparisons",
    actionLabel: "Read guide",
  },
];

const categories = ["All", "Summarization", "Transcription", "Study Tools", "Productivity", "Comparisons"];

const AllTools: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [filteredTools, setFilteredTools] = useState(tools);

  useEffect(() => {
    let result = tools;

    if (selectedCategory !== "All") {
      result = result.filter((tool) => tool.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (tool) =>
          tool.name.toLowerCase().includes(query) ||
          tool.description.toLowerCase().includes(query)
      );
    }

    setFilteredTools(result);
  }, [searchQuery, selectedCategory]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Free AI Tools - YouWo AI",
    "description": "Collection of free AI-powered productivity tools including video summarizers, transcribers, flashcard makers, and more.",
    "hasPart": tools.map((tool) => ({
      "@type": tool.category === "Comparisons" ? "Article" : "WebApplication",
      "name": tool.name,
      "description": tool.description,
      "url": `https://youwo.ai${tool.href}`,
    })),
  };

  return (
    <>
      <SEOHead
        title="Free AI Tools - Video Summarizer, PDF Summarizer & More | YouWo AI"
        description="Explore our collection of free AI-powered tools. YouTube summarizer, PDF summarizer, audio transcriber, flashcard maker, quiz generator, and more. No sign-up required."
        keywords="free ai tools, youtube summarizer, pdf summarizer, audio transcriber, flashcard maker, quiz generator, ai productivity tools"
        canonicalUrl="https://youwo.ai/tools"
        structuredData={structuredData}
      />

      <main className="yw-consumer-page yw-tools-page yw-tools-shell" data-lang="en" data-variant="tools">
        <ToolsLandingNav links={[
          { label: "Summarize", href: "#tools" },
          { label: "Study", href: "#tools" },
          { label: "Sources", href: "#tools" },
        ]} />

        <section className="yw-tools-index-hero">
          <h1>
            Free AI tools for <span>source-backed</span> study work
          </h1>
          <p>
            Summarize videos, transcribe audio, generate flashcards, build quizzes, and turn PDFs into study guides.
          </p>

          <div className="yw-tools-search-shell">
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search YouWo tools..."
              size="lg"
              startContent={<Icon icon="lucide:search" className="text-xl text-gray-400" />}
              classNames={{
                input: "text-base",
                inputWrapper: "border-gray-200 hover:border-cornflowerblue focus-within:border-cornflowerblue shadow-none bg-white/70",
              }}
            />
          </div>
        </section>

        <div className="yw-tools-filter-bar" aria-label="Tool category filters">
          {categories.map((category) => (
            <button
              aria-pressed={selectedCategory === category}
              key={category}
              onClick={() => setSelectedCategory(category)}
              type="button"
            >
              {category}
            </button>
          ))}
        </div>

        {selectedCategory === "All" && !searchQuery ? (
          <section className="yw-tools-index-section">
            <div className="yw-tools-section-heading">
              <h2>Start with the common source jobs.</h2>
              <p>These are the workflows students and teams usually need first.</p>
            </div>

            <div className="yw-tools-index-grid">
              {tools
                .filter((tool) => tool.popular)
                .map((tool) => (
                  <ToolCard key={tool.href} tool={tool} featured />
                ))}
            </div>
          </section>
        ) : null}

        <section className="yw-tools-index-section" id="tools">
          <div className="yw-tools-section-heading">
            <h2>{selectedCategory === "All" ? "All tools" : selectedCategory}</h2>
            <p>{filteredTools.length} tool{filteredTools.length === 1 ? "" : "s"} available.</p>
          </div>

          {filteredTools.length > 0 ? (
            <div className="yw-tools-index-grid">
              {filteredTools.map((tool) => (
                <ToolCard key={tool.href} tool={tool} />
              ))}
            </div>
          ) : (
            <div className="yw-tools-empty-state">
              <Icon icon="lucide:search-x" />
              <h3>No tools found</h3>
              <p>Try another search or filter.</p>
            </div>
          )}
        </section>

        <section className="yw-tools-final-cta">
          <div>
            <h2>Use the tools with the full YouWo workspace.</h2>
            <p>Open YouWo to save outputs, ask follow-up questions, and keep sources connected across notes, recordings, PDFs, and chats.</p>
            <a href={buildAppLink()} target="_blank" rel="noopener noreferrer">
              Open YouWo
              <Icon icon="lucide:arrow-up-right" />
            </a>
          </div>
        </section>
      </main>

      <div className="yw-tools-footer-shell">
        <ModernFooter />
      </div>
    </>
  );
};

const ToolCard: React.FC<{ tool: Tool; featured?: boolean }> = ({ tool, featured = false }) => (
  <Link className={`yw-tools-index-card ${featured ? "yw-tools-index-card-featured" : ""}`} to={tool.href}>
    <Icon icon={tool.icon} className={`yw-tools-index-card-icon ${tool.iconColor} ${tool.bgColor}`} />
    <h3>
      {tool.name}
      {tool.popular ? <span className="yw-tools-popular-chip">Popular</span> : null}
    </h3>
    <small>{tool.category}</small>
    <p>{tool.description}</p>
    <span>
      {tool.actionLabel ?? "Open tool"}
      <Icon icon="lucide:arrow-right" />
    </span>
  </Link>
);

export default AllTools;
